import { useQuery } from "@tanstack/react-query";
import { FolderInput, Pencil } from "lucide-react";
import type { DocPageEvent, DocRevision } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { FeedbackButton } from "@/components/feedback/FeedbackButton";
import { LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { formatAbsolute, relativeTime } from "@/modules/docs/versions";
import { useDocsPeople } from "@/modules/docs/useDocsPeople";

type TimelineEntry =
  | { kind: "revision"; at: string; revision: DocRevision }
  | { kind: "event"; at: string; event: DocPageEvent };

/** Newest first, across both kinds of entry — a move can land between two saves. */
function mergeTimeline(
  revisions: readonly DocRevision[],
  events: readonly DocPageEvent[],
): TimelineEntry[] {
  const entries: TimelineEntry[] = [
    ...revisions.map((revision) => ({
      kind: "revision" as const,
      at: revision.createdAt,
      revision,
    })),
    ...events.map((event) => ({
      kind: "event" as const,
      at: event.occurredAt,
      event,
    })),
  ];
  return entries.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
}

function eventLine(event: DocPageEvent): string {
  return event.type === "moved"
    ? `Moved from ${event.before} to ${event.after}`
    : `Renamed from ${event.before} to ${event.after}`;
}

/**
 * Every saved revision of one page (FR-DOC-4), interleaved with its move and
 * rename history (FR-DOC-10) — newest first, a right-side sheet on a desktop
 * and a bottom sheet on a phone where a right-side panel would be a sliver
 * nobody could read.
 *
 * A move or a rename is not a row you can click into: there is no content
 * behind it to view or restore, only a fact about where the page was or what
 * it was called (`DocPageEvent`'s own comment). No diff here either
 * (2026-09-27: the owner dropped FR-DOC-5, see §7.8), and no restore button
 * on a revision row — restoring only makes sense once a revision is actually
 * on screen to look at, which is what clicking one does (`onView`, handled
 * by `PageReader`'s "viewing an old version" banner).
 */
export function VersionsSheet({
  pageId,
  currentRevisionId,
  viewingRevisionId,
  open,
  onOpenChange,
  onView,
}: {
  pageId: string;
  currentRevisionId: string;
  viewingRevisionId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onView: (revisionId: string) => void;
}) {
  const data = useDataSource();
  const people = useDocsPeople();
  const isMobile = useIsMobile();

  // Shares its cache with the revision `PageReader` looks up to render the
  // banner — same query key, one request either way `useQuery` sees it first.
  const revisionsQuery = useQuery({
    queryKey: ["docs", "revisions", pageId],
    queryFn: () => data.docs.listRevisions(pageId, { limit: 100 }),
  });
  const eventsQuery = useQuery({
    queryKey: ["docs", "events", pageId],
    queryFn: () => data.docs.listPageEvents(pageId, { limit: 100 }),
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        title="Versions"
        description="Every saved revision of this page, and every time it was moved or renamed, newest first."
        side={isMobile ? "bottom" : "right"}
        actions={<FeedbackButton />}
        className={isMobile ? undefined : "w-full sm:w-[420px] sm:max-w-[90vw]"}
      >
        {revisionsQuery.isPending || eventsQuery.isPending ? (
          <div className="p-4">
            <TableSkeleton rows={4} />
          </div>
        ) : revisionsQuery.isError || eventsQuery.isError ? (
          <div className="p-4">
            <LoadFailed
              message={
                (revisionsQuery.isError
                  ? revisionsQuery.error.message
                  : undefined) ??
                (eventsQuery.isError ? eventsQuery.error.message : undefined) ??
                "Failed to load."
              }
              onRetry={() => {
                void revisionsQuery.refetch();
                void eventsQuery.refetch();
              }}
            />
          </div>
        ) : (
          <ul className="divide-border divide-y">
            {mergeTimeline(
              revisionsQuery.data.items,
              eventsQuery.data.items,
            ).map((entry) =>
              entry.kind === "revision" ? (
                <li key={`rev-${entry.revision.id}`}>
                  <button
                    type="button"
                    onClick={() => {
                      onView(entry.revision.id);
                    }}
                    className={cn(
                      "flex w-full flex-col items-start gap-0.5 px-4 py-3 text-left text-sm transition-colors hover:bg-muted/50 sm:px-6",
                      viewingRevisionId === entry.revision.id && "bg-accent",
                    )}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className="tabular-nums"
                        title={formatAbsolute(entry.revision.createdAt)}
                      >
                        {relativeTime(entry.revision.createdAt)}
                      </span>
                      {entry.revision.id === currentRevisionId ? (
                        <Badge variant="secondary" className="font-normal">
                          Current
                        </Badge>
                      ) : null}
                    </div>
                    <p className="text-muted-foreground">
                      {people.principalName(
                        "user",
                        entry.revision.authorUserId,
                      )}
                      {entry.revision.message
                        ? ` — ${entry.revision.message}`
                        : null}
                    </p>
                  </button>
                </li>
              ) : (
                <li
                  key={`evt-${entry.event.id}`}
                  className="flex items-start gap-2 px-4 py-3 text-sm sm:px-6"
                >
                  {entry.event.type === "moved" ? (
                    <FolderInput
                      className="text-muted-foreground mt-0.5 size-4 shrink-0"
                      aria-hidden
                    />
                  ) : (
                    <Pencil
                      className="text-muted-foreground mt-0.5 size-4 shrink-0"
                      aria-hidden
                    />
                  )}
                  <div className="min-w-0 space-y-0.5">
                    <span
                      className="tabular-nums"
                      title={formatAbsolute(entry.event.occurredAt)}
                    >
                      {relativeTime(entry.event.occurredAt)}
                    </span>
                    <p className="text-muted-foreground">
                      {eventLine(entry.event)} by{" "}
                      {people.principalName("user", entry.event.actorUserId)}
                    </p>
                  </div>
                </li>
              ),
            )}
          </ul>
        )}
      </SheetContent>
    </Sheet>
  );
}
